import React from 'react';
import AeroLogo from '../Aero/AeroLogo';
import AeroIcon from '../Aero/AeroIcon';
import AeroPlanMeter from '../Aero/AeroPlanMeter';

/**
 * AeroShellSidebar: the v3 sidebar (research/v3 shared/aero.css .sb-*), as a
 * pure view. AeroSidebar feeds it real data; /aero feeds it Example data.
 *
 *   "Aero." with the collapse arrow beside it (no workspace name)
 *   Home, Sites, Tests; up to 5 recent tests under Tests with a state dot
 *     (green: open, ring: closed or draft)
 *   the plan meter above the account; the account bottom left
 * Collapsed (`collapsed`), it is a 56px icon rail: the "A." mark, the icons,
 * the meter as a line and the avatar; the arrow flips to bring it back. On
 * screens up to 1024px the layout hides it instead (the mobile menu button
 * opens it), exactly as before.
 *
 * Props:
 *   active        'home' | 'sites' | 'tests' | 'users' | null
 *   openTestId    id of the test whose page is open (marks it and Tests as parent)
 *   showHome      false where "/" is not Aero's Home (non-Aero hosts)
 *   counts        { sites, tests } (numbers or null)
 *   recentTests   [{ id, name, state: 'open' | 'closed' | 'draft', example,
 *                 sessions, newCount }]: a test with new sessions gets the blue
 *                 dot and "N new", and opens on its Sessions tab; the open test
 *                 shows its session count (as in the product demo)
 *   plan          { label, used, limit }
 *   account       { name, email, example }
 *   isAdmin       shows the admin Users item
 *   onNavigate(path), onToggle(), onPlanClick()
 *   demo          true in the style guide: a static block, without the layout's
 *                 .ut-sidebar sizing and mobile overlay rules
 */
function Count({ n }) {
  return typeof n === 'number' ? <span className="aero-sb-ct">{n}</span> : null;
}

export default function AeroShellSidebar({
  collapsed = false,
  active = null,
  openTestId = null,
  showHome = true,
  counts = {},
  recentTests = [],
  plan = null,
  account = {},
  isAdmin = false,
  homePath = '/',
  onNavigate = () => {},
  onToggle,
  onPlanClick,
  demo = false,
  className = '',
}) {
  const item = (key, label, icon, path, count) => {
    const parent = key === 'tests' && openTestId;
    const state = active === key ? (parent ? ' parent' : ' on') : '';
    return (
      <button
        type="button"
        className={`aero-sb-item${state}`}
        onClick={() => onNavigate(path)}
        title={collapsed ? label : undefined}
        aria-current={active === key && !parent ? 'page' : undefined}
      >
        <AeroIcon name={icon} />
        <span className="aero-sb-l">{label}</span>
        <Count n={count} />
      </button>
    );
  };

  const initial = (account.name || account.email || 'A').charAt(0).toUpperCase();
  const recent = recentTests.slice(0, 5);

  return (
    <aside
      className={[
        demo ? 'aero-sb aero-sb-demo' : 'ut-sidebar aero-sb',
        collapsed ? (demo ? 'is-rail' : 'is-rail ut-sidebar-collapsed') : '',
        className,
      ].filter(Boolean).join(' ')}
    >
      <div className="aero-sb-top">
        <button type="button" className="aero-sb-logo" onClick={() => onNavigate(homePath)} aria-label="Aero, go to Home">
          <AeroLogo variant={collapsed ? 'mark' : 'lockup'} size="sm" title="Aero" />
        </button>
        {onToggle && (
          <button
            type="button"
            className="aero-sb-collapse"
            onClick={onToggle}
            title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
            aria-label={collapsed ? 'Show sidebar' : 'Hide sidebar'}
          >
            <AeroIcon name="left" />
          </button>
        )}
      </div>

      <nav className="aero-sb-nav" aria-label="Aero">
        {showHome && item('home', 'Home', 'home', homePath)}
        {item('sites', 'Sites', 'site', '/sites', counts.sites)}
        {item('tests', 'Tests', 'test', '/user-testing', counts.tests)}
        {recent.length > 0 && (
          <div className="aero-sb-sub">
            {recent.map((t) => {
              const isNew = t.newCount > 0;
              const open = openTestId === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`aero-sb-si${open ? ' on' : ''}`}
                  onClick={() => onNavigate(isNew ? `/user-testing/${t.id}/sessions` : `/user-testing/${t.id}`)}
                  aria-current={open ? 'page' : undefined}
                  title={collapsed ? t.name : undefined}
                  aria-label={isNew ? `${t.name}, ${t.newCount} new session${t.newCount === 1 ? '' : 's'}` : undefined}
                >
                  <span className={`aero-sb-st${isNew ? ' new' : t.state === 'open' ? '' : ' off'}`} aria-hidden="true" />
                  <span className="aero-sb-nm">{t.name}</span>
                  {t.example && <span className="aero-sb-tag">Example</span>}
                  {isNew ? (
                    <span className="aero-sb-new">{t.newCount} new</span>
                  ) : open && typeof t.sessions === 'number' ? (
                    <span className="aero-sb-ct">{t.sessions}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
        {isAdmin && item('users', 'Users', 'users', '/users')}
      </nav>

      <div className="aero-sb-foot">
        {plan && (
          <AeroPlanMeter
            className="aero-sb-plan"
            plan={plan.label}
            used={plan.used}
            limit={plan.limit}
            onClick={onPlanClick}
          />
        )}
        <button
          type="button"
          className="aero-sb-acct"
          onClick={() => onNavigate('/account')}
          title={collapsed ? (account.name || account.email || 'Account') : 'Account'}
        >
          <span className="aero-sb-av" aria-hidden="true">{initial}</span>
          <span className="aero-sb-who">
            <b>{account.name || 'Account'}</b>
            {account.email && <small>{account.email}</small>}
          </span>
          {account.example && <span className="aero-sb-ex">Example</span>}
        </button>
      </div>
    </aside>
  );
}
